import { queryKeys, useQueryClient, type QueryClient } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { formatNumber, useToast } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { TFunction } from 'i18next';
import { useApiErrorMessage } from './useApiError.ts';

export type TableLabels = {
  readonly previous: string;
  readonly next: string;
  readonly range: (from: number, to: number, total: number) => string;
};

export type PageContext = {
  readonly t: TFunction;
  readonly locale: string;
  readonly tenantId: string;
  readonly notify: (message: string) => void;
  readonly warn: (message: string) => void;
  readonly queryClient: QueryClient;
  readonly describeError: (error: unknown) => string | undefined;
  readonly tableLabels: TableLabels;
  readonly invalidate: (...resources: readonly string[]) => Promise<void>;
};

export const usePageContext = (): PageContext => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const describe = useApiErrorMessage();
  const tenantId = auth.tenantId ?? 'platform';

  return {
    t,
    locale: i18n.language,
    tenantId,
    queryClient,
    notify: (message: string) => {
      toast.notify({ tone: 'success', message });
    },
    warn: (message: string) => {
      toast.notify({ tone: 'warning', message });
    },
    describeError: (error: unknown) => describe(error)?.message,
    tableLabels: {
      previous: t('table.previous'),
      next: t('table.next'),
      range: (from: number, to: number, total: number) =>
        t('table.range', {
          from: formatNumber(from, i18n.language),
          to: formatNumber(to, i18n.language),
          total: formatNumber(total, i18n.language),
        }),
    },
    invalidate: async (...resources: readonly string[]) => {
      await Promise.all(
        resources.map((resource) => queryClient.invalidateQueries({ queryKey: [resource, tenantId] })),
      );
    },
  };
};

export { queryKeys };
